// A dónde volver después de restablecer la contraseña, cuando se pidió DESDE el alta
// por link. Ver ADR-2026-09-23-el-reset-de-clave-no-abandona-el-checkout.
//
// El problema: `/alta-empresa/:plan` manda el mail con un `redirectTo` pelado a
// `/restablecer-clave`, y esa pantalla siempre termina en `/auth` pelado. La persona
// pierde el plan que había elegido, el `?pais=` que decide moneda y proveedor de cobro,
// el `?desc=` del cupón y el `?sid=` que linkea el embudo — justo en la pantalla que va
// a ser la entrada principal.
//
// Va por `localStorage` y NO por la URL del mail a propósito. Meterlo en el `redirectTo`
// sería mejor (cruza de dispositivo), pero Supabase valida esa URL contra la lista de
// Redirect URLs y, si el patrón configurado no contempla el query, NO falla: manda al
// Site URL. O sea que el riesgo de equivocarse no es perder el plan, es que la persona
// no pueda cambiar la contraseña. Con localStorage el peor caso es exactamente lo que
// pasa hoy (terminar en `/auth`), así que es una mejora estricta. Para pasarlo a la URL
// primero hay que verificar el patrón en Authentication → URL Configuration.

const CLAVE = 'atlas:vuelta-al-alta';

/**
 * Cuánto vale lo anotado. El link de recuperación de Supabase dura una hora, así que
 * pasada esa ventana lo guardado ya no corresponde a ningún reset en curso.
 *
 * Hace falta porque el destino se anota ANTES de mandar el mail y se consume DESPUÉS de
 * cambiar la contraseña: un reset abandonado a la mitad dejaba el valor ahí para
 * siempre, y el próximo reset hecho desde otro lado en el mismo navegador se lo llevaba
 * puesto, mandando a alguien a un plan viejo en vez de al login.
 */
const VIGENCIA_MS = 60 * 60 * 1000;

interface VueltaGuardada {
  ruta: string;
  ts: number;
}

/**
 * ¿Lo anotado sigue correspondiendo a un reset en curso? Pura para testearla sin reloj.
 */
export function vueltaVigente(ts: number, ahora: number): boolean {
  return Number.isFinite(ts) && ahora - ts >= 0 && ahora - ts < VIGENCIA_MS;
}

/**
 * ¿Es una ruta del alta por link a la que se puede volver?
 *
 * Pura y exportada para poder testearla (`vitest.config.ts`: environment "node").
 * Lista blanca y no lista negra: lo que sale de acá se usa como destino de navegación,
 * y el valor viene de `localStorage`, que el usuario puede editar. `//evil.com` no pasa
 * el prefijo; el chequeo explícito está igual porque es la clase de cosa que un refactor
 * del prefijo podría abrir sin querer.
 */
export function esRutaDeAltaPorLink(ruta: string | null | undefined): boolean {
  const r = (ruta ?? '').trim();
  if (!r.startsWith('/alta-empresa/')) return false;
  if (r.startsWith('//')) return false;
  return !r.includes('\\') && !/[\u0000-\u001f]/.test(r);
}

/**
 * Se llama al arrancar CUALQUIER recuperación de contraseña, con la ruta desde la que
 * se pidió. Si es del alta por link la anota; si no, **borra** lo que hubiera.
 *
 * Esa segunda mitad es la que importa: sin ella, un reset del alta abandonado a la
 * mitad dejaba el destino guardado, y el próximo reset pedido desde `/auth`, desde
 * `AuthCompat` o desde Tiendanube en el mismo navegador lo consumía y mandaba a esa
 * persona a un plan que no estaba comprando. Por eso los cuatro puntos de recuperación
 * llaman a esta misma función y no sólo el del alta.
 */
export function recordarVueltaDelReset(rutaConQuery: string): void {
  try {
    if (!esRutaDeAltaPorLink(rutaConQuery)) {
      localStorage.removeItem(CLAVE);
      return;
    }
    const guardada: VueltaGuardada = { ruta: rutaConQuery, ts: Date.now() };
    localStorage.setItem(CLAVE, JSON.stringify(guardada));
  } catch {
    /* modo privado o storage lleno: se pierde el destino, no el reset */
  }
}

/**
 * Después de cambiar la contraseña: a dónde ir, o `null` si no hay nada anotado.
 * Se consume UNA vez — si no, el próximo reset desde cualquier otro lado terminaría
 * mandando a un plan que esta persona ya no está comprando.
 */
export function vueltaAlAltaGuardada(): string | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    localStorage.removeItem(CLAVE);
    if (!crudo) return null;
    const guardada = JSON.parse(crudo) as Partial<VueltaGuardada> | null;
    const ruta = typeof guardada?.ruta === 'string' ? guardada.ruta : null;
    const ts = typeof guardada?.ts === 'number' ? guardada.ts : NaN;
    if (!esRutaDeAltaPorLink(ruta) || !vueltaVigente(ts, Date.now())) return null;
    return ruta;
  } catch {
    // JSON roto, o un valor viejo del formato anterior (string pelado): se descarta.
    return null;
  }
}
